import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageNavigator } from '../components/ui/PageNavigator';
import { CheckCircle, ArrowRight, BarChart3, MapPin, Users, Zap, Target, ClipboardCheck, Settings, FileText, Globe, Activity, Truck, Thermometer, AlertTriangle } from 'lucide-react';
import { VisualBreak } from '../components/ui/VisualBreak';
import { KPIStrip } from '../components/ui/KPIStrip';
import { PowerStatement } from '../components/ui/PowerStatement';

export const Projects = () => {
  const sections = [
    { id: 'case-studies', label: 'Project Case Studies' },
    { id: 'execution-metrics', label: 'Performance Metrics' },
    { id: 'engagement-model', label: 'Execution Models' },
    { id: 'why-energipro', label: 'Why EnergiPro' },
  ];

  return (
    <Layout>
      <SeoHead
        title="Project Case Studies | Well Testing & Field Execution Records"
        description="Detailed project case studies: 7 wells in 62 days, 18 km pipeline in 28 days, 12 fabrication skids in 90 days. Verified NPT, schedule adherence, and safety metrics."
        canonicalPath="/projects"
      />
      {/* Hero Section */}
      <div className="bg-slate-900 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-l-4 border-orange-600 pl-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">EXECUTION EXPERIENCE</h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-4xl leading-relaxed mb-6">
              Documented project execution — well testing, nitrogen pumping, hydrostatic testing, fabrication, and logistics — across Saudi Arabia, UAE, and Egypt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button href="/request-quote" variant="primary" className="px-8 py-3">
                SUBMIT RFQ
              </Button>
              <Button href="/files/energipro-capability-statement.pdf" variant="outline-light" className="px-8 py-3">
                REQUEST TECHNICAL CAPABILITY PACKAGE
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Execution Metrics KPI Strip */}
      <KPIStrip
        background="accent"
        items={[
          { value: '48–72', suffix: ' hrs', label: 'Average Mobilization' },
          { value: '0', label: 'Recordable Incidents' },
          { value: '98', suffix: '%+', label: 'Data Acceptance Rate' },
          { value: '0', label: 'Non-Productive Time' },
        ]}
      />

      <PageNavigator sections={sections} />

      {/* ===== PROJECT CASE STUDIES ===== */}
      <Section id="case-studies">
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">PROJECT CASE STUDIES</h2>
          <p className="text-slate-600 max-w-3xl">Verified execution records. Specific equipment, conditions, and measurable outcomes documented below.</p>
        </div>

        {/* Project 1 */}
        <div className="mb-16">
          <div className="border-l-4 border-orange-600 pl-6 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Surface Well Testing — 7-Well Campaign, 62 Days Continuous</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600"><MapPin size={14} className="text-orange-600" /> Eastern Province, KSA</span>
              <span className="flex items-center gap-1 text-slate-600"><Users size={14} className="text-orange-600" /> Major National Operator (KSA)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/field-oilfield-choke.jpg" alt="Choke manifold deployed on wellsite" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">10K Choke Manifold</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-test-separator.png" alt="3-Phase test separator deployed at wellsite" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">3-Phase Separator (1,440 PSI WP)</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/field-gas-flare.jpg" alt="Flare stack operations" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">H₂S-Rated Flare Boom</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Scope & Scale</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 7 wells tested sequentially</li>
                <li>• 62 days continuous operations</li>
                <li>• 3-phase separation & EWT</li>
                <li>• Back-to-back rig-up/rig-down</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Equipment Deployed</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Choke manifold: 10,000 PSI WP, 3-1/16" bore</li>
                <li>• Test separator: 1,440 PSI WP, 10,000 BFPD</li>
                <li>• Flare boom: H₂S rated, 80 MMSCFD</li>
                <li>• Data acquisition: 1-sec P/T/flow logging</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Execution Conditions</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Ambient: 38–52°C (summer ops)</li>
                <li>• Remote desert location, 180 km from hub</li>
                <li>• H₂S present: 50–200 ppm confirmed</li>
                <li>• 24/7 continuous — day/night crew rotation</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Deliverables</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Certified pressure test charts (per well)</li>
                <li>• SCADA data logs (1-sec interval export)</li>
                <li>• End-of-well QC reports</li>
                <li>• Equipment traceability records</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Performance</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• NPT: 0 hrs (zero non-productive time)</li>
                <li>• Schedule: 100% adherence</li>
                <li>• Data acceptance: all 7 wells first-pass</li>
                <li>• TRIR: 0.00 for campaign duration</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Project 2 */}
        <div className="mb-16">
          <div className="border-l-4 border-orange-600 pl-6 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">HP Nitrogen Pumping & Pipeline Hydrostatic Testing — 18 km, 28 Days</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600"><MapPin size={14} className="text-orange-600" /> Western Region, KSA</span>
              <span className="flex items-center gap-1 text-slate-600"><Users size={14} className="text-orange-600" /> Tier-1 EPC Contractor</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-hp-pump-unit.png" alt="High pressure pumping unit on trailer" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">HP Pump Unit (15K PSI)</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-n2-pump-unit.png" alt="Nitrogen pump unit containerized" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">3× N₂ Pump Units Deployed</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-well-testing-unit.png" alt="Well testing equipment unit" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Chart Recorders & Manifold</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Scope & Scale</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 18 km pipeline (4 test sections)</li>
                <li>• 28 days total duration</li>
                <li>• Nitrogen purging + hydro test</li>
                <li>• Pressure verification & certification</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Equipment Deployed</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 3× HP nitrogen units: 10,000 PSI, 1,200 SCFM</li>
                <li>• Test manifold assembly: 15,000 PSI WP</li>
                <li>• Certified chart recorders (API 598)</li>
                <li>• HP iron: 2" & 3" 1502 connections</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Execution Conditions</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Ambient: 28–45°C</li>
                <li>• Remote ROW access — limited road infrastructure</li>
                <li>• No H₂S — inert gas environment</li>
                <li>• 24-hr continuous pumping cycles</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Deliverables</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Certified hydrostatic test charts (per section)</li>
                <li>• Pressure recorder data logs (ASME B31.3)</li>
                <li>• Equipment calibration certificates</li>
                <li>• Traceability records (iron, fittings, gauges)</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Performance</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• All 4 sections passed first attempt</li>
                <li>• NPT: 0 hrs (equipment-related)</li>
                <li>• Delivered 2 days ahead of schedule</li>
                <li>• Zero incidents — 0.00 TRIR</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Project 3 */}
        <div className="mb-16">
          <div className="border-l-4 border-orange-600 pl-6 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Engineering, Fabrication & Skid Assembly — 12 Skid Packages, 90 Days</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600"><MapPin size={14} className="text-orange-600" /> Dammam / Jubail, KSA</span>
              <span className="flex items-center gap-1 text-slate-600"><Users size={14} className="text-orange-600" /> Tier-1 Service Company</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/asset-fabrication.jpg" alt="Fabrication workshop operations" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">ASME-Compliant Fabrication</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/asset-welding.jpg" alt="Welding and assembly operations" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Certified Welders (AWS D1.1 / ASME IX)</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/logistics-transport-truck.jpg" alt="Skid transport to site" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Pressure Tested Before Dispatch</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Scope & Scale</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 12 skid packages fabricated</li>
                <li>• 90 days workshop execution</li>
                <li>• Piping spools + structural frames</li>
                <li>• DNV 2.7-1 compliant lifting frames</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">In-House Capability</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• ASME B31.3 process piping fabrication</li>
                <li>• Certified welders: ASME IX / AWS D1.1</li>
                <li>• NDT: MPI, UT, RT (third-party verified)</li>
                <li>• Hydrostatic testing: up to 15,000 PSI</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Execution Conditions</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Workshop environment (controlled)</li>
                <li>• Concurrent welding & assembly on 4 bays</li>
                <li>• Material sourced locally (KSA mills)</li>
                <li>• Client QC rep on-site throughout</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Deliverables</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Weld maps with welder ID traceability</li>
                <li>• NDT reports (100% RT on critical welds)</li>
                <li>• Hydro test certificates per skid</li>
                <li>• Final data book (MTR, NDE, dimensional)</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Performance</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 12/12 skids delivered on schedule</li>
                <li>• NDE rejection rate: &lt;2%</li>
                <li>• All hydro tests passed first attempt</li>
                <li>• Zero rework after client QC inspection</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Project 4 */}
        <div className="mb-16">
          <div className="border-l-4 border-orange-600 pl-6 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Pressure Control Equipment Supply — 45-Day Delivery Program</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600"><MapPin size={14} className="text-orange-600" /> UAE / Egypt</span>
              <span className="flex items-center gap-1 text-slate-600"><Users size={14} className="text-orange-600" /> Tier-1 Service Company / EPC</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/field-oilfield-choke.jpg" alt="Pressure control equipment" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">API 6A Components (10K–15K)</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-cross-region.png" alt="Cross-border equipment delivery" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Cross-Border Delivery (UAE → Egypt)</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-equipment-inspection.png" alt="Equipment incoming inspection" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Incoming Inspection & Certification</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Scope & Scale</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 24 line items, 3 purchase orders</li>
                <li>• 45-day delivery program</li>
                <li>• Chokes, valves, instrumentation</li>
                <li>• Critical spares + capital equipment</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Equipment Supplied</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• API 6A choke valves: 10K & 15K PSI</li>
                <li>• Gate valves: 3-1/8" – 7-1/16" bore</li>
                <li>• OEM data acquisition modules</li>
                <li>• Pressure transmitters (0–15K PSI range)</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Execution Conditions</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Cross-border coordination: UAE → Egypt</li>
                <li>• Customs clearance in 2 jurisdictions</li>
                <li>• OEM lead times managed (4–6 wks)</li>
                <li>• Client inspection at Abu Dhabi warehouse</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Deliverables</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• MTRs (material test reports) per item</li>
                <li>• Certificates of conformity (CoC)</li>
                <li>• Incoming inspection reports</li>
                <li>• Full traceability pack (heat no., batch no.)</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Performance</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 24/24 items delivered within schedule</li>
                <li>• Zero rejection at incoming inspection</li>
                <li>• Documentation 100% complete at delivery</li>
                <li>• DDP Incoterms — full responsibility to site</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Project 5 */}
        <div className="mb-8">
          <div className="border-l-4 border-orange-600 pl-6 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Equipment Mobilization — 4 Sites, 3 Regions, 35 Days</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1 text-slate-600"><MapPin size={14} className="text-orange-600" /> KSA (Eastern, Western, Central)</span>
              <span className="flex items-center gap-1 text-slate-600"><Users size={14} className="text-orange-600" /> Major National Operator (KSA)</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/logistics-heavy-transport.jpg" alt="Heavy transport mobilization" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Lowboy: 80T Payload Capacity</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-fleet-mobilization.png" alt="Fleet mobilization convoy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">8 Loads per Mobilization Cycle</span>
            </div>
            <div className="relative overflow-hidden rounded-sm h-48 md:h-56">
              <img src="/assets/project-cross-region-new.png" alt="Cross-region logistics coordination" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <span className="absolute bottom-3 left-3 text-white text-xs font-semibold">Cross-Region Coordination</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Scope & Scale</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 4 wellsites across 3 KSA regions</li>
                <li>• 35 days total mob/demob cycle</li>
                <li>• 32 truck loads (8 per site)</li>
                <li>• Full test spread per location</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Equipment Moved</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Separators: 15T per unit (skid-mounted)</li>
                <li>• Choke manifolds: 10K PSI, crated</li>
                <li>• Frac tanks: 500 BBL × 8 units</li>
                <li>• HP piping bundles & flare systems</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Execution Conditions</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Inter-regional highway transit (400–800 km)</li>
                <li>• Oversized loads requiring escort & permits</li>
                <li>• Night transport mandated (high-temp hours)</li>
                <li>• Staging at Dammam yard between cycles</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Deliverables</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• Mobilization plans (load lists, route plans)</li>
                <li>• Equipment condition reports (pre/post)</li>
                <li>• Transport method statements</li>
                <li>• Delivery confirmation with photo records</li>
              </ul>
            </div>
            <div className="bg-slate-50 p-5 rounded-sm border border-slate-200">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Performance</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>• 32/32 loads delivered on schedule</li>
                <li>• Zero cargo damage incidents</li>
                <li>• Zero transport safety incidents</li>
                <li>• All equipment operational on arrival</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Visual Break before metrics */}
      <VisualBreak
        image="/assets/showcase/batch-4/wellsite-separator-surge-tank.png"
        statement="Zero NPT. Certified data. On-schedule delivery."
        height="sm"
      />

      {/* ===== EXECUTION METRICS ===== */}
      <Section id="execution-metrics" background="dark">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">PERFORMANCE METRICS</h2>
            <p className="text-slate-400 text-sm">Measurable outcomes from documented project execution.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">0 hrs</div>
              <div className="text-sm text-slate-300 font-medium">Equipment NPT</div>
              <div className="text-xs text-slate-500 mt-1">Across well testing campaigns</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">100%</div>
              <div className="text-sm text-slate-300 font-medium">Schedule Adherence</div>
              <div className="text-xs text-slate-500 mt-1">All documented projects delivered on/ahead of schedule</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">0.00</div>
              <div className="text-sm text-slate-300 font-medium">TRIR</div>
              <div className="text-xs text-slate-500 mt-1">Total Recordable Incident Rate (field ops)</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">100%</div>
              <div className="text-sm text-slate-300 font-medium">Data Acceptance</div>
              <div className="text-xs text-slate-500 mt-1">First-pass client acceptance on test data</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">15K</div>
              <div className="text-sm text-slate-300 font-medium">Max PSI Handled</div>
              <div className="text-xs text-slate-500 mt-1">API 6A / 16C rated equipment</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">3</div>
              <div className="text-sm text-slate-300 font-medium">Countries</div>
              <div className="text-xs text-slate-500 mt-1">KSA, UAE, Egypt — cross-border execution</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">48 hrs</div>
              <div className="text-sm text-slate-300 font-medium">Fastest Mobilization</div>
              <div className="text-xs text-slate-500 mt-1">Hub to wellsite (Eastern Province)</div>
            </div>
            <div className="bg-slate-800/60 border border-slate-700 rounded-sm p-6 text-center">
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">&lt;2%</div>
              <div className="text-sm text-slate-300 font-medium">NDE Rejection Rate</div>
              <div className="text-xs text-slate-500 mt-1">Fabrication weld quality (third-party verified)</div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== EXECUTION MODELS ===== */}
      <Section id="engagement-model" background="light">
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">EXECUTION MODELS BASED ON OPERATIONAL REQUIREMENTS</h2>
          <p className="text-slate-600 max-w-3xl">Engagement structure determined by project duration, equipment commitment, and operational scope.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="p-8 border-t-4 border-orange-600">
            <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center mb-4">
              <Zap size={24} className="text-orange-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-3">Call-Out / Spot Hire</h3>
            <p className="text-sm text-slate-600 mb-4">Equipment + crew mobilized for single-well or short-scope requirements.</p>
            <ul className="text-sm text-slate-700 space-y-2">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> 48–72 hr mobilization from yard</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Day-rate or lump-sum pricing</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Our equipment, our operators</li>
            </ul>
          </Card>
          <Card className="p-8 border-t-4 border-slate-900">
            <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center mb-4">
              <Target size={24} className="text-slate-700" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-3">Campaign Execution</h3>
            <p className="text-sm text-slate-600 mb-4">Multi-well or multi-section scope with defined deliverables and closeout package.</p>
            <ul className="text-sm text-slate-700 space-y-2">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Defined well count / pipeline km</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Milestone reporting & data delivery</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> End-of-campaign documentation pack</li>
            </ul>
          </Card>
          <Card className="p-8 border-t-4 border-slate-900">
            <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center mb-4">
              <Globe size={24} className="text-slate-700" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-3">Term Contract / Dedicated Fleet</h3>
            <p className="text-sm text-slate-600 mb-4">Equipment allocated exclusively to client over 6–24 month term with maintenance & crew rotation.</p>
            <ul className="text-sm text-slate-700 space-y-2">
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Dedicated spread — no shared allocation</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Scheduled maintenance & crew rotation</li>
              <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-600 mt-0.5" /> Monthly performance reporting</li>
            </ul>
          </Card>
        </div>
      </Section>

      {/* ===== WHY ENERGIPRO ===== */}
      <Section id="why-energipro">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">WHY ENERGIPRO</h2>
            <p className="text-slate-600">Differentiators relevant to contractor evaluation and prequalification.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-sm border border-slate-200">
              <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center shrink-0">
                <Settings size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Operational Control</h4>
                <p className="text-sm text-slate-600">We own the equipment. We operate it with our crews. No sub-rental. No third-party operators. Single point of execution responsibility.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-sm border border-slate-200">
              <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center shrink-0">
                <Zap size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Mobilization Capability</h4>
                <p className="text-sm text-slate-600">Equipment yards in Dammam, Dubai, Cairo. Own transport fleet. 48–72 hr mobilization from RFQ acceptance. No logistics dependency.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-sm border border-slate-200">
              <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center shrink-0">
                <ClipboardCheck size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Standards Compliance</h4>
                <p className="text-sm text-slate-600">Equipment maintained per API 6A / 16C intervals. Fabrication per ASME B31.3 / IX. NDT per ASNT SNT-TC-1A. IMS-aligned operations.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-6 bg-slate-50 rounded-sm border border-slate-200">
              <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center shrink-0">
                <FileText size={20} className="text-white" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Documentation Traceability</h4>
                <p className="text-sm text-slate-600">Certified test charts, SCADA data exports, weld maps, NDE reports, MTRs, calibration certs, equipment history — all traceable, all auditable.</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== FINAL CTA ===== */}
      <Section background="dark">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">SUBMIT RFQ OR REQUEST TECHNICAL CAPABILITY PACKAGE</h2>
          <p className="text-slate-400 mb-8">Commercial team responds within 24 business hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/request-quote" variant="primary" className="px-8 py-4 text-lg">
              SUBMIT RFQ
            </Button>
            <Button href="/files/energipro-capability-statement.pdf" variant="outline-light" className="px-8 py-4 text-lg">
              DOWNLOAD TECHNICAL CAPABILITY PACKAGE
            </Button>
          </div>
        </div>
      </Section>
    </Layout>
  );
};
